import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import moment from 'moment';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Director from '../../services/director';
import { Floppy, Trash } from 'react-bootstrap-icons';
import FormInput from '../../components/FormInput';
import SearchBar from '../SearchBar';
import Content from '../Content';
import AlertBox from '../AlertBox';
import LoadingPage from '../LoadingPage';

import CommentList from '../CommentList';
import FileList from '../FileList';

export default function PageDirectorEdit() {
    const [id, setId] = useState(useParams().id);
    const [disableDelete, setDisableDelete] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [reload, setReload] = useState(true);
    const [alert, setAlert] = useState(null);
    const [director, setDirector] = useState({});
    const [uploadResponseAlert, setUploadResponseAlert] = useState([]);

    const [submitting, setSubmitting] = useState(false);

    const [loading, setLoading] = useState(true);
    const [directorLoading, setDirectorLoading] = useState(true);

    useEffect(() => {
        setLoading(directorLoading)
    }, [directorLoading]);

    useEffect(() => {
        setSubmitting(false);
    }, [alert, uploadResponseAlert]);

    const mounted = useRef(true);

    useEffect(() => { document.title = "Edit Director"; }, []);

    useEffect(() => {
        mounted.current = true;
        if (!reload) {
            return;
        }

        Director.getById(id)
            .then(item => {
                if (mounted.current) {
                    setDirector(item);
                    setDisableDelete(item.ProjectUnits && item.ProjectUnits.length > 0);
                    setDirectorLoading(false);
                    setReload(false);
                    setSubmitting(false);
                }
            })
            .catch(e => {
                //console.log(e);
                window.location.href = "/notfound";
            });
        return () => mounted.current = false;
    }, [reload]);

    useEffect(() => {
        if (isDirty) {
            setAlert(null);
            window.addEventListener("beforeunload", beforeUnloadHandler);
        }
        return () => {
            window.removeEventListener("beforeunload", beforeUnloadHandler);
        }
    }, [isDirty]);

    const beforeUnloadHandler = (event) => {
        event.preventDefault();
        event.returnValue = true;
    };

    const onFieldChanged = (field, value) => {
        if (director) {
            setIsDirty(true);
            setDirector({ ...director, [field]: value });
        }
    }

    const onSave = () => {
        setSubmitting(true);
        Director.addUpdate(director)
            .then(item => {
                if (mounted.current) {
                    if (item.error) {
                        return setAlert({ message: item.error, isError: true });
                    }

                    setId(item.id);
                    setIsDirty(false);
                    setDirector(item);
                    setAlert({ message: `Saved`, isError: false });
                }
            });
    }

    const onDelete = () => {
        if (window.confirm("Are you sure you want to delete?") === true) {
            setSubmitting(true);
            setIsDirty(false);
            Director.delete(director).then(item => {
                if (item.error) {
                    setAlert({ message: 'Unable to delete', isError: true });
                    return;
                }
                window.location.href = '/directors'
            });
        } else {
            // Do nothing
        }
    }

    const capitalizeInput = (value) => {
        return value.toUpperCase();
    }

    return (
        <LoadingPage loading={loading} submitting={submitting}>
            <SearchBar>
                <Row style={{ display: 'flex', alignItems: 'center' }}>
                    <Col sm style={{ display: 'flex', alignItems: 'center', fontStyle: 'bold' }}>
                        <a href='/directors' style={{ paddingRight: '5px', paddingLeft: '5px' }}>Directors</a> /
                        <span style={{ paddingRight: '5px', paddingLeft: '5px' }}>{director.name}</span>
                    </Col>
                    {id !== '0' && !disableDelete && <Col sm="auto"><Button variant="outline-danger" onClick={() => onDelete()} title='Delete' disabled={submitting}><Trash size={30} /></Button></Col>}
                    <Col sm="auto"><Button variant="outline-success" onClick={() => onSave()} title='Save' disabled={submitting}><Floppy size={30} /></Button></Col>
                </Row>
            </SearchBar>
            <Content>
                <Row>
                    <Col>
                        <Row>
                            <Col lg>
                                <FormInput value={director.firstname || ''} label='First Name' onChange={(e) => onFieldChanged('firstname', e.target.value)} />
                            </Col>
                            <Col lg>
                                <FormInput value={director.middlename || ''} label='Middle Name' onChange={(e) => onFieldChanged('middlename', e.target.value)} />
                            </Col>
                            <Col lg>
                                <FormInput value={director.lastname || ''} label='Last Name' onChange={(e) => onFieldChanged('lastname', e.target.value)} />
                            </Col>
                            <Col lg>
                                <FormInput type="date" value={moment(director.dateOfBirth).format('YYYY-MM-DD') || ''} label='Date of Birth' onChange={(e) => onFieldChanged('dateOfBirth', e.target.value)} />
                            </Col>
                        </Row>
                        <Row>
                            <Col lg>
                                <FormInput value={director.contactNumber || ''} label='Contact Number' onChange={(e) => onFieldChanged('contactNumber', e.target.value)} />
                            </Col>
                            <Col lg>
                                <FormInput value={director.email || ''} label='Email' onChange={(e) => onFieldChanged('email', e.target.value)} />
                            </Col>
                        </Row>
                        <Row>
                            <Col lg>
                                <FormInput value={director.address || ''} label='Address' onChange={(e) => onFieldChanged('address', e.target.value)} />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormInput value={director.city || ''} label='City' onChange={(e) => onFieldChanged('city', e.target.value)} />
                            </Col>
                            <Col>
                                <FormInput value={director.district || ''} label='District' onChange={(e) => onFieldChanged('district', e.target.value)} />
                            </Col>
                            <Col>
                                <FormInput value={director.state || ''} label='State' onChange={(e) => onFieldChanged('state', e.target.value)} />
                            </Col>
                        </Row>
                        <Row>
                            <Col lg>
                                <FormInput value={director.correspondenceAddress || ''} label='Correspondence Address' onChange={(e) => onFieldChanged('correspondenceAddress', e.target.value)} />
                            </Col>
                        </Row>
                        <Row>
                            <Col lg>
                                <FormInput value={director.aadhaarNumber || ''} label='Aadhaar Number' onChange={(e) => onFieldChanged('aadhaarNumber', e.target.value)} />
                            </Col>
                            <Col lg>
                                <FormInput value={director.panNumber || ''} label='PAN Number' onChange={(e) => onFieldChanged('panNumber', capitalizeInput(e.target.value))} />
                            </Col>
                        </Row>
                        <FormInput value={director.details || ''} label='Details' onChange={(e) => onFieldChanged('details', e.target.value)} as='textarea' style={{ height: '100px' }} />
                    </Col>
                    <Col sm='3'>
                        <FileList modelId={id} modelType='director' />
                    </Col>
                    <Col sm='auto'>
                        {director.Projects && director.Projects.length > 0 &&
                            <Row className='mb-2'>
                                <Col>
                                    <Card>
                                        <Card.Header style={{ textAlign: 'center' }}>Projects</Card.Header>
                                        <Card.Body>
                                            {director.Projects.map((u, i) =>
                                                <Card key={i} className='mb-2' bg='success' text='white' style={{cursor: 'pointer'}} onClick={() => window.location.href = `/projects/${u.id}`}>
                                                    <Card.Body>
                                                        <Card.Title>{u.name}</Card.Title>
                                                    </Card.Body>
                                                </Card>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        }
                        <Row><Col><CommentList modelId={id} /></Col></Row>

                    </Col>
                </Row>
            </Content>

            {alert &&
                <AlertBox isError={alert.isError} onClose={() => setAlert(null)}>
                    {alert.message}
                </AlertBox>}

            {/* <div style={{textAlign: 'left'}}><pre>
                {JSON.stringify(director, null, 2)}
            </pre></div> */}
        </LoadingPage>
    )
}